import React, { useMemo } from "react";
import "./styles.scss";
import { Box } from "@material-ui/core";
import {
  IExperiencesAgeBandCounts,
  IExperiencesPassengerCounts,
  IExperiencesTravelerBandCounts,
} from "redmond";
import clsx from "clsx";
import { max, min } from "lodash-es";
import { ActionButton, ActionLink, Icon, IconName } from "halifax";

export const MINIMUM_ADULTS_COUNT = 1;
export const MAXIMUM_TRAVELERS_COUNT = 6;

export type PassengerCountLimits = AgeBandCountLimits | TravelerCountLimits;

export interface AgeBandCountLimits {
  seniorsCount?: { min?: number; max?: number };
  adultsCount: { min?: number; max?: number };
  youthsCount?: { min?: number; max?: number };
  childrenCount: { min?: number; max?: number };
  infantsCount?: { min?: number; max?: number };
}

export interface TravelerCountLimits {
  travelersCount?: { min?: number; max?: number };
}

export type PassengerCountPickerType = IExperiencesPassengerCounts;

const getSeniorsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IExperiencesPassenegerCounts
  if ("seniorsCount" in counts) {
    return counts.seniorsCount || 0;
  }
  return 0;
};

const getAdultsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IExperiencesPassenegerCounts
  if ("adultsCount" in counts) {
    return counts.adultsCount;
  }
  return 0;
};

const getTravelerCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IExperiencesPassenegerCounts
  if ("travelerCount" in counts) {
    return counts.travelerCount;
  }
  return 0;
};

const getYouthsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IExperiencesPassenegerCounts
  if ("youthsCount" in counts) {
    return counts.youthsCount || 0;
  }
  return 0;
};

const getChildrenCount = (counts: PassengerCountPickerType) => {
  if ("childrenCount" in counts) {
    return counts.childrenCount || 0;
  }

  return 0;
};

const getInfantsCount = (counts: PassengerCountPickerType) => {
  // TODO: fix compiler issue where using isPassengerCounts function does not help resolving counts as type of IExperiencesPassenegerCounts
  if ("infantsCount" in counts) {
    return counts.infantsCount || 0;
  }
  return 0;
};

export type CountPickerTitles = {
  modalTitle: string;
};

export interface TravelerCountPickerTitles extends CountPickerTitles {
  travelerTitle: string;
  travelerPrice?: (count: number) => string;
  travelerSubtitle: string;
  travelerHelperText?: string;
}

export interface AgeBandCountPickerTitles extends CountPickerTitles {
  seniorTitle?: string;
  seniorPrice?: (count: number) => string;
  seniorSubtitle?: string;
  seniorHelperText?: string;
  adultTitle: string;
  adultPrice?: (count: number) => string;
  adultSubtitle: string;
  adultHelperText?: string;
  youthTitle?: string;
  youthPrice?: (count: number) => string;
  youthSubtitle?: string;
  youthHelperText?: string;
  childrenTitle: string;
  childrenPrice?: (count: number) => string;
  childrenSubtitle?: string;
  childrenHelperText?: string;
  childAgeInputSubtitle?: string;
  infantSeatTitle?: string;
  infantSeatSubtitle?: string;
  infantLapTitle?: string;
  infantLapSubtitle?: string;
  infantTitle?: string;
  infantSubtitle?: string;
  infantHelperText?: string;
  infantPrice?: (count: number) => string;
}

export type PassengerCountPickerTitles =
  | AgeBandCountPickerTitles
  | TravelerCountPickerTitles;

export interface IPassengerCountPickerProps {
  onClickApply?: (counts: PassengerCountPickerType) => void;
  setPassengerCounts: (passengerCounts: PassengerCountPickerType) => void;
  counts: PassengerCountPickerType;
  minimumAdultsCount?: number;
  maximumChildrenCount?: number;
  maximumRoomsCount?: number;
  minimumTravelersCount?: number;
  maximumTravelersCount: number;
  titles: PassengerCountPickerTitles;
  warningMessage?: string;
  unlimited?: boolean;
  className?: string;
  buttonText?: string;
  enableButtonOnChangeOnly?: boolean;
  showChildAgeInputs?: boolean;
  showInfantAgeInputs?: boolean;
  getPaxCountsError?: (counts: PassengerCountPickerType) => string | null; // Returns an error string if pax counts are invalid
  paxCountLimits?: PassengerCountLimits;
}

export const PassengerCountPicker = (props: IPassengerCountPickerProps) => {
  const { titles, counts, className, paxCountLimits } = props;

  return (
    <Box className={clsx("passenger-count-picker-root", className)}>
      <Box className="passenger-count-picker-container">
        <Box
          id="passenger-count-picker"
          className="passenger-count-picker-title"
        >
          {titles.modalTitle}
        </Box>
        {"travelerCount" in counts ? (
          <TravelerCountPicker
            {...props}
            counts={counts as IExperiencesTravelerBandCounts}
            // setPassengerCounts={function (
            //   passengerCounts: IExperiencesTravelerBandCounts
            // ): void {
            //   throw new Error("Function not implemented.");
            // }}
            titles={titles as TravelerCountPickerTitles}
            paxCountLimits={paxCountLimits as TravelerCountLimits}
          />
        ) : (
          <AgeBandCountPicker
            {...props}
            counts={counts as IExperiencesAgeBandCounts}
            // setPassengerCounts={function (
            //   passengerCounts: IExperiencesAgeBandCounts
            // ): void {
            //   throw new Error("Function not implemented.");
            // }}
            titles={titles as AgeBandCountPickerTitles}
            paxCountLimits={paxCountLimits as AgeBandCountLimits}
          />
        )}
      </Box>
    </Box>
  );
};

export interface AgeBandCountPickerProps {
  onClickApply?: (counts: IExperiencesAgeBandCounts) => void;
  setPassengerCounts: (passengerCounts: IExperiencesAgeBandCounts) => void;
  counts: IExperiencesAgeBandCounts;
  minimumAdultsCount?: number;
  maximumChildrenCount?: number;
  maximumRoomsCount?: number;
  minimumTravelersCount?: number;
  maximumTravelersCount: number;
  titles: AgeBandCountPickerTitles;
  warningMessage?: string;
  className?: string;
  buttonText?: string;
  enableButtonOnChangeOnly?: boolean;
  showChildAgeInputs?: boolean;
  showInfantAgeInputs?: boolean;
  getPaxCountsError?: (counts: PassengerCountPickerType) => string | null; // Returns an error string if pax counts are invalid
  paxCountLimits?: AgeBandCountLimits;
}

export const AgeBandCountPicker = (props: AgeBandCountPickerProps) => {
  const {
    onClickApply,
    setPassengerCounts,
    getPaxCountsError,
    counts,
    titles,
    minimumAdultsCount = MINIMUM_ADULTS_COUNT,
    minimumTravelersCount = MINIMUM_ADULTS_COUNT,
    maximumTravelersCount = MAXIMUM_TRAVELERS_COUNT,
    warningMessage = "",
    className,
    buttonText,
    enableButtonOnChangeOnly = false,
    paxCountLimits,
  } = props;

  const [seniorsCount, setSeniorsCount] = React.useState<number>(
    getSeniorsCount(counts)
  );

  const [adultsCount, setAdultsCount] = React.useState<number>(
    getAdultsCount(counts)
  );
  const [youthsCount, setYouthsCount] = React.useState<number>(
    getYouthsCount(counts)
  );

  const [infantsCount, setInfantsCount] = React.useState<number>(
    getInfantsCount(counts)
  );
  const [childrenCount, setChildrenCount] = React.useState<number>(
    getChildrenCount(counts)
  );

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const isTooManyPassengers =
    adultsCount + childrenCount + seniorsCount + youthsCount + infantsCount >
    maximumTravelersCount;

  const isTooLittleTravelers =
    minimumTravelersCount !== undefined &&
    minimumTravelersCount !== 0 &&
    adultsCount + childrenCount + seniorsCount + youthsCount + infantsCount <
      minimumTravelersCount;

  const applyDisabled = isTooManyPassengers || isTooLittleTravelers;

  const remainingLimitedTravelersCount =
    max([
      maximumTravelersCount -
        (childrenCount +
          adultsCount +
          youthsCount +
          seniorsCount +
          infantsCount),
      0,
    ]) || 0;

  const handleSetPassengerCounts = () => {
    if ("adultsCount" in counts) {
      setPassengerCounts({
        seniorsCount,
        adultsCount,
        youthsCount,
        childrenCount,
        infantsCount,
      });
    }
  };

  const paxCountsUpdated = useMemo(() => {
    return (
      counts.adultsCount !== adultsCount ||
      counts.seniorsCount !== seniorsCount ||
      counts.youthsCount !== youthsCount ||
      counts.infantsCount !== infantsCount ||
      counts.childrenCount !== childrenCount
    );
  }, [adultsCount, seniorsCount, youthsCount, infantsCount, childrenCount]);

  return (
    <Box>
      <Box className={clsx("passenger-count-picker-types-section")}>
        {titles.seniorTitle &&
          renderCountPick({
            title: titles.seniorTitle,
            subtitle: titles.seniorSubtitle,
            helperText: titles.seniorHelperText,
            price: titles.seniorPrice?.(seniorsCount),
            className: titles.seniorSubtitle || titles.seniorTitle,
            minimumCount: paxCountLimits?.seniorsCount?.min || 0,
            maximumCount: (() => {
              switch (true) {
                default:
                  return min([
                    remainingLimitedTravelersCount + seniorsCount,
                    paxCountLimits?.seniorsCount?.max,
                  ]);
              }
            })(),
            count: seniorsCount,
            setCount: setSeniorsCount,
          })}
        {renderCountPick({
          title: titles.adultTitle,
          subtitle: titles.adultSubtitle,
          helperText: titles.adultHelperText,
          price: titles.adultPrice?.(adultsCount),
          className: titles.adultSubtitle,
          minimumCount:
            minimumAdultsCount || paxCountLimits?.adultsCount?.min || 0,
          maximumCount: (() => {
            switch (true) {
              default:
                return min([
                  remainingLimitedTravelersCount + adultsCount,
                  paxCountLimits?.adultsCount?.max,
                ]);
            }
          })(),
          count: adultsCount,
          setCount: setAdultsCount,
        })}
        {titles.youthTitle &&
          renderCountPick({
            title: titles.youthTitle,
            subtitle: titles.youthSubtitle,
            helperText: titles.youthHelperText,
            price: titles.youthPrice?.(youthsCount),
            className: titles.youthSubtitle || titles.youthTitle,
            minimumCount: paxCountLimits?.youthsCount?.min || 0,
            maximumCount: (() => {
              switch (true) {
                default:
                  return min([
                    remainingLimitedTravelersCount + youthsCount,
                    paxCountLimits?.youthsCount?.max,
                  ]);
              }
            })(),
            count: youthsCount,
            setCount: setYouthsCount,
          })}
        {titles.childrenTitle &&
          renderCountPick({
            title: titles.childrenTitle,
            price: titles.childrenPrice?.(childrenCount),
            subtitle: titles.childrenSubtitle,
            helperText: titles.childrenHelperText,
            className: "children",
            count: childrenCount,
            setCount: setChildrenCount,
            maximumCount: min([
              remainingLimitedTravelersCount + childrenCount,
              paxCountLimits?.childrenCount?.max,
            ]),
          })}
        {titles.infantTitle &&
          renderCountPick({
            title: titles.infantTitle,
            subtitle: titles.infantSubtitle,
            helperText: titles.infantHelperText,
            price: titles.infantPrice?.(infantsCount),
            className: titles.infantSubtitle || titles.infantTitle,
            minimumCount: 0,
            maximumCount: min([
              remainingLimitedTravelersCount + infantsCount,
              paxCountLimits?.infantsCount?.max,
            ]),
            count: infantsCount,
            setCount: setInfantsCount,
          })}
      </Box>
      {errorMessage && renderErrorMessage(errorMessage)}
      {renderWarningMessage({
        maximumTravelersCount,
        warningMessage,
      })}
      <ActionButton
        className={clsx("passenger-count-change-button-wrapper", className)}
        buttonClassName="passenger-count-change-button"
        disabled={
          (!enableButtonOnChangeOnly && applyDisabled) ||
          (enableButtonOnChangeOnly && !paxCountsUpdated)
        }
        message={buttonText || "Apply"}
        onClick={() => {
          const errorMessage = getPaxCountsError
            ? getPaxCountsError({
                seniorsCount,
                adultsCount,
                youthsCount,
                childrenCount: childrenCount,
                infantsCount,
              })
            : null;
          setErrorMessage(errorMessage);
          if (!errorMessage) {
            handleSetPassengerCounts();
            onClickApply?.({
              seniorsCount,
              adultsCount,
              youthsCount,
              childrenCount,
              infantsCount,
            });
          }
        }}
      />
    </Box>
  );
};

export interface TravelerCountPickerProps {
  onClickApply?: (counts: IExperiencesTravelerBandCounts) => void;
  setPassengerCounts: (passengerCounts: IExperiencesTravelerBandCounts) => void;
  counts: IExperiencesTravelerBandCounts;
  minimumAdultsCount?: number;
  maximumChildrenCount?: number;
  maximumRoomsCount?: number;
  minimumTravelersCount?: number;
  maximumTravelersCount: number;
  titles: TravelerCountPickerTitles;
  warningMessage?: string;
  className?: string;
  buttonText?: string;
  enableButtonOnChangeOnly?: boolean;
  showChildAgeInputs?: boolean;
  showInfantAgeInputs?: boolean;
  getPaxCountsError?: (counts: IExperiencesTravelerBandCounts) => string | null; // Returns an error string if pax counts are invalid
  paxCountLimits?: TravelerCountLimits;
}

export const TravelerCountPicker = (props: TravelerCountPickerProps) => {
  const {
    onClickApply,
    setPassengerCounts,
    getPaxCountsError,
    counts,
    titles,
    minimumAdultsCount = MINIMUM_ADULTS_COUNT,
    minimumTravelersCount = MINIMUM_ADULTS_COUNT,
    maximumTravelersCount = MAXIMUM_TRAVELERS_COUNT,
    warningMessage = "",
    className,
    buttonText,
    enableButtonOnChangeOnly = false,
    paxCountLimits,
  } = props;

  const [travelerCount, setTravelerCount] = React.useState<number>(
    getTravelerCount(counts)
  );

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const isTooManyPassengers = travelerCount > maximumTravelersCount;

  const isTooLittleTravelers =
    minimumTravelersCount !== undefined &&
    minimumTravelersCount !== 0 &&
    travelerCount < minimumTravelersCount;

  const applyDisabled = isTooManyPassengers || isTooLittleTravelers;

  const remainingLimitedTravelersCount =
    max([maximumTravelersCount - travelerCount, 0]) || 0;

  const handleSetPassengerCounts = () => {
    if ("travelerCount" in counts) {
      setPassengerCounts({
        travelerCount,
      });
    }
  };

  const paxCountsUpdated = useMemo(() => {
    return counts.travelerCount !== travelerCount;
  }, [travelerCount]);

  return (
    <Box>
      <Box className={clsx("passenger-count-picker-types-section")}>
        {renderCountPick({
          title: titles.travelerTitle,
          subtitle: titles.travelerSubtitle,
          helperText: titles.travelerHelperText,
          price: titles.travelerPrice?.(travelerCount),
          className: titles.travelerSubtitle,
          minimumCount:
            minimumAdultsCount || paxCountLimits?.travelersCount?.min || 0,
          maximumCount: (() => {
            switch (true) {
              default:
                return min([
                  remainingLimitedTravelersCount + travelerCount,
                  paxCountLimits?.travelersCount?.max,
                ]);
            }
          })(),
          count: travelerCount,
          setCount: setTravelerCount,
        })}
      </Box>
      {errorMessage && renderErrorMessage(errorMessage)}
      {renderWarningMessage({
        maximumTravelersCount,
        warningMessage,
      })}
      <ActionButton
        className={clsx("passenger-count-change-button-wrapper", className)}
        buttonClassName="passenger-count-change-button"
        disabled={
          (!enableButtonOnChangeOnly && applyDisabled) ||
          (enableButtonOnChangeOnly && !paxCountsUpdated)
        }
        message={buttonText || "Apply"}
        onClick={() => {
          const errorMessage = getPaxCountsError
            ? getPaxCountsError({
                travelerCount,
              })
            : null;
          setErrorMessage(errorMessage);
          if (!errorMessage) {
            handleSetPassengerCounts();
            onClickApply?.({
              travelerCount,
            });
          }
        }}
      />
    </Box>
  );
};

const renderErrorMessage = (message: string) => {
  if (!message) return;

  return <Box className="passenger-count-picker-error-message">{message}</Box>;
};

const renderWarningMessage = (props: {
  maximumTravelersCount?: number;
  warningMessage?: string;
}) => {
  const { maximumTravelersCount, warningMessage } = props;

  const getDisplayMessage = () => {
    if (warningMessage) return warningMessage;
    if (maximumTravelersCount)
      return `You can search for up to ${maximumTravelersCount} travelers`;
    return "";
  };

  return (
    <Box className="passenger-count-picker-warning-message">
      {getDisplayMessage()}
    </Box>
  );
};

const renderCountPick = (props: {
  title: string;
  subtitle?: string | JSX.Element;
  helperText?: string | JSX.Element;
  price?: string;
  className: string;
  minimumCount?: number;
  maximumCount?: number;
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const {
    title,
    subtitle,
    helperText,
    price,
    className,
    count,
    setCount,
    minimumCount,
    maximumCount,
  } = props;

  const isMinusDisabled = count <= (minimumCount ?? 0);
  const isPlusDisabled = count >= (maximumCount ?? Infinity);

  return (
    <Box className={clsx("passenger-count-picker-type", className)}>
      <Box className="passenger-type-description-container">
        <Box className="passenger-type-description">
          <Box className="passenger-type-title">{title}</Box>
          {price && <Box className="passenger-type-price">{price}</Box>}
          {subtitle && (
            <Box className="passenger-type-subtitle">{subtitle}</Box>
          )}
          {helperText && (
            <Box className="passenger-type-helper-text">{helperText}</Box>
          )}
        </Box>
      </Box>
      <Box className="passenger-count-picker-container">
        <ActionLink
          label={`Decrease ${title} ${subtitle || ""} Traveler`}
          className={clsx("passenger-count-picker-button", "minus")}
          onClick={() => {
            if (!isMinusDisabled) {
              setCount((prevCount) => prevCount - 1);
            }
          }}
          content={
            <Box
              className={clsx("button-content-wrapper", {
                disabled: isMinusDisabled,
              })}
            >
              <Icon
                name={IconName.MinusIcon}
                className={clsx("button-content minus-icon", {
                  disabled: isMinusDisabled,
                })}
              />
            </Box>
          }
        />
        <Box className="passenger-count-number-container">
          <Box className="passenger-count-number">
            {count}
            <span
              className="passenger-count-alert"
              aria-live="polite"
              aria-atomic={true}
            >
              {subtitle ? `${count} ${title} ${subtitle}` : `${count} ${title}`}
            </span>
          </Box>
        </Box>

        <ActionLink
          label={`Increase ${title} ${subtitle || ""} Traveler`}
          className={clsx("passenger-count-picker-button", "plus")}
          onClick={() => {
            if (!isPlusDisabled) {
              setCount((prevCount) => prevCount + 1);
            }
          }}
          content={
            <Box
              className={clsx("button-content-wrapper", {
                disabled: isPlusDisabled,
              })}
            >
              <Icon
                name={IconName.PlusIcon}
                className={clsx("button-content plus-icon", {
                  disabled: isPlusDisabled,
                })}
              />
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
